import { useState } from 'react'
import { authApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'

/**
 * Логика формы входа.
 *
 * Куда вести после успешного входа — решает приложение: у водителя
 * это «/», у консоли «/admin», а водителя, попавшего в консоль,
 * нужно отправить на соседний хост. Прежняя общая страница делала
 * `navigate(role === 'admin' ? '/admin' : '/')`, что после разделения
 * приложений неверно в обоих случаях.
 *
 * @param {(user: object) => void} onSuccess
 */
export function useLoginForm(onSuccess) {
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login } = useAuthStore()

  const submit = async (e) => {
    e?.preventDefault?.()
    setError('')

    if (!phone.trim()) {
      setError('Введите номер телефона')
      return false
    }
    if (!password) {
      setError('Введите пароль')
      return false
    }

    setLoading(true)
    try {
      const { data } = await authApi.login(phone, password)
      const { user, access_token, refresh_token } = data.data
      login(user, access_token, refresh_token)
      onSuccess?.(user)
      return true
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось войти. Проверьте номер и пароль.')
      return false
    } finally {
      setLoading(false)
    }
  }

  return {
    phone,
    setPhone,
    password,
    setPassword,
    error,
    setError,
    loading,
    submit,
    /** Подставить учётку целиком — используется отладочной панелью. */
    prefill: ({ phone: p, password: pw }) => {
      setPhone(p)
      setPassword(pw)
      setError('')
    },
  }
}

/**
 * Демонстрационные учётки для стенда.
 *
 * ВАЖНО: оба условия вычисляются Vite на этапе сборки, поэтому
 * в обычном продакшен-бандле остаётся пустой массив, а строки
 * с паролями вырезаются минификатором целиком.
 *
 * Прежняя версия держала этот список безусловно — пароли
 * администратора и менеджера уезжали в продакшен-бандл и читались
 * любым, кто открыл исходники страницы. Скрывался только их показ,
 * а не наличие.
 *
 * VITE_DEMO_ACCOUNTS=1 включает панель и в собранном приложении —
 * это нужно демонстрационному стенду, который собирается тем же
 * прод-образом. Флаг по умолчанию выключен и задаётся ровно в одном
 * месте, в docker-compose.yml: включить его случайно нельзя,
 * а увидеть в конфигурации — сразу.
 */
const DEMO_ENABLED = import.meta.env.DEV
  || import.meta.env.VITE_DEMO_ACCOUNTS === '1'

export const DEMO_ACCOUNTS = DEMO_ENABLED
  ? [
      { label: 'Администратор', phone: '+79000000001', password: 'admin123' },
      { label: 'Менеджер', phone: '+79000000002', password: 'manager123' },
      { label: 'Водитель', phone: '+79000000003', password: 'driver123' },
      { label: 'Водитель 2', phone: '+79000000007', password: 'driver2_123' },
      { label: 'На проверке', phone: '+79000000004', password: 'pending123' },
      { label: 'Черновик', phone: '+79000000005', password: 'draft123' },
      { label: 'Заблокирован', phone: '+79000000006', password: 'blocked123' },
      { label: 'Отклонён', phone: '+79000000008', password: 'rejected123' },
    ]
  : []
